import React from 'react'
import { Col, Row, Button } from 'reactstrap'
import moment from 'moment'
import { withFirebase } from '../../components/Fb'
import { DataGrid } from '@mui/x-data-grid'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import ActivityCreator from '../../components/Details/ActivityCreator'
import { DEFAULT_SPORTS, DEFAULT_TYPES } from '../../sports'

// Aktiviteetit > Toistuvat (docs/features/recurring-activities.md): the recurring series,
// recurrents/{id} with collection 'activities'. The backend generates a series' occurrences into
// activities; a draft series has none, so this is the only place it can be found and finished.
// Drafts can be edited, published and deleted here. A published series is read-only: its
// occurrences already exist, and they are edited from the activity list ("Tätä ja kaikkia
// tulevia").

const INTERVAL_LABELS = { daily: 'Päivittäin', weekly: 'Viikoittain', monthly: 'Kuukausittain' }
const STATE_LABELS = { draft: 'Luonnos', published: 'Julkaistu', cancelled: 'Peruttu' }
const WEEKDAYS = ['', 'ma', 'ti', 'ke', 'to', 'pe', 'la', 'su']

// The weekday the creator generated the dates for: it is not stored on the series, but its
// occurrence rows (or else the start date) carry it.
const seriesWeekday = (series) => {
  const first = Array.isArray(series.recurrencies) && series.recurrencies[0] && series.recurrencies[0].starttime
  const date = first || (series.recurrency && series.recurrency.startdate)
  const m = date ? moment.utc(String(date).slice(0, 10), 'YYYY-MM-DD') : null
  return m && m.isValid() ? m.isoWeekday() : 1
}

const formatDate = (value) => (value ? moment.utc(value, 'YYYY-MM-DD').format('D.M.YYYY') : '')

class RecurringActivitiesContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      series: [],
      occurrenceCounts: {},
      editing: null,
      deleteTarget: null,
      notices: {},
      images: [],
      sports: DEFAULT_SPORTS,
      activitytypes: DEFAULT_TYPES,
    }
  }

  componentDidMount() {
    const { firebase } = this.props
    this.unsubscribers = [
      firebase.listenRecurrentActivities().onSnapshot((snapshot) => {
        const series = snapshot.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }))
          .filter((s) => s.state !== 'deleted')
          .sort((a, b) => String((b.recurrency && b.recurrency.startdate) || '').localeCompare(String((a.recurrency && a.recurrency.startdate) || '')))
        this.setState({ series, loading: false })
      }),
      firebase.listenMedia().onSnapshot((snapshot) => {
        const images = []
        snapshot.forEach((doc) => {
          if ((doc.data().mimetype || '').indexOf('image/') > -1) images.push({ id: doc.id, ...doc.data() })
        })
        this.setState({ images })
      }),
      firebase.getSports().onSnapshot((doc) => this.setState({ sports: mergeOptions(DEFAULT_SPORTS, doc) })),
      firebase.getActivitytypes().onSnapshot((doc) => this.setState({ activitytypes: mergeOptions(DEFAULT_TYPES, doc) })),
    ]
    this.countOccurrences()
  }

  componentWillUnmount() {
    ;(this.unsubscribers || []).forEach((unsubscribe) => unsubscribe && unsubscribe())
    if (this.recountTimer) clearTimeout(this.recountTimer)
  }

  countOccurrences = async () => {
    const snapshot = await this.props.firebase.getActivities()
    const occurrenceCounts = {}
    snapshot.docs.forEach((doc) => {
      const id = doc.data().recurrentid
      if (id) occurrenceCounts[id] = (occurrenceCounts[id] || 0) + 1
    })
    this.setState({ occurrenceCounts })
    return occurrenceCounts
  }

  setNotice = (id, text) => this.setState({ notices: { ...this.state.notices, [id]: text } })

  // Saving a draft from the editor updates the same series. Switching Tila to published there
  // publishes it in place too: the backend generates the occurrences on draft -> published.
  saveDraft = (activity) => {
    const id = this.state.editing.id
    if (activity.state === 'published') return this.publish({ ...activity, id }, { ...activity, ispublished: true })
    return this.props.firebase.updateRecurrentActivity(id, activity)
  }

  // Publishing a draft is an update of the same series doc: the backend generates the
  // occurrences when a series goes from draft to published (docs/functions-to-update.txt,
  // "recurrents: draft series and publishing"). fields: what to write with it - the editor
  // sends the whole edited series.
  publish = async (series, fields = { state: 'published', ispublished: true }) => {
    this.setNotice(series.id, 'Julkaistaan…')
    try {
      await this.props.firebase.updateRecurrentActivity(series.id, { ...fields, state: 'published', ispublished: true })
    } catch (error) {
      console.error('Publishing the series failed', error)
      this.setNotice(series.id, 'Julkaisu epäonnistui.')
      return
    }
    this.checkOccurrences(series.id)
  }

  // "Luo kerrat": a series published before the backend generated occurrences on draft ->
  // published has none, and an update of a published series does not trigger generation. So it
  // is created again as a new, published series - generation runs as for any new series - and
  // the old doc is retired (state 'deleted', replacedBy).
  recreate = async (series) => {
    const { firebase } = this.props
    this.setNotice(series.id, 'Luodaan kertoja…')
    let created
    try {
      const copy = JSON.parse(JSON.stringify(series))
      ;['id', 'created', 'updated', 'updaterId', 'creatorId', 'timestamp', 'date', 'replacedBy'].forEach((field) => delete copy[field])
      created = await firebase.createRecurrentActivity({ ...copy, state: 'published', ispublished: true })
      await firebase.updateRecurrentActivity(series.id, { state: 'deleted', replacedBy: created.id })
    } catch (error) {
      console.error('Recreating the series failed', error)
      this.setNotice(series.id, 'Kertojen luonti epäonnistui.')
      return
    }
    this.checkOccurrences(created.id)
  }

  // A moment later, so a series the backend did not pick up is not left looking published
  // but empty.
  checkOccurrences = (id) => {
    this.setNotice(id, 'Julkaistu. Tarkistetaan kertoja…')
    this.recountTimer = setTimeout(async () => {
      const counts = await this.countOccurrences()
      this.setNotice(id, counts[id] ? 'Julkaistu, ' + counts[id] + ' kertaa luotu.' : 'Julkaistu, mutta kertoja ei ole vielä luotu.')
    }, 6000)
  }

  // A published series without occurrences that has not ended yet: published before the backend
  // generated occurrences on draft -> published, so it never got any.
  needsOccurrences = (row) => {
    if (row.state !== 'published' || (this.state.occurrenceCounts[row.id] || 0) > 0) return false
    const enddate = row.recurrency && row.recurrency.enddate
    return !enddate || enddate >= moment.utc().format('YYYY-MM-DD')
  }

  confirmDelete = async () => {
    const target = this.state.deleteTarget
    this.setState({ deleteTarget: null })
    if (target) await this.props.firebase.updateRecurrentActivity(target.id, { state: 'deleted' })
  }

  columns = () => [
    {
      headerName: '',
      field: 'actions',
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row
        if (this.needsOccurrences(row)) {
          return (
            <Button size='sm' color='info' title='Sarjalle ei luotu kertoja julkaistaessa' onClick={() => this.recreate(row)}>
              Luo kerrat
            </Button>
          )
        }
        if (row.state !== 'draft') {
          return (
            <Typography variant='caption' color='text.secondary'>
              Muokkaa kertoja aktiviteettilistasta
            </Typography>
          )
        }
        const count = this.state.occurrenceCounts[row.id] || 0
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
            <Button size='sm' color='success' onClick={() => this.setState({ editing: row })}>
              Muokkaa
            </Button>
            <Button size='sm' color='info' onClick={() => this.publish(row)}>
              Julkaise
            </Button>
            {count === 0 ? (
              <Button size='sm' color='danger' onClick={() => this.setState({ deleteTarget: row })}>
                Poista
              </Button>
            ) : null}
          </Box>
        )
      },
    },
    { headerName: 'Nimi', field: 'name', width: 220, valueGetter: (value) => (value && value.fi) || '' },
    { headerName: 'Laji', field: 'sport', width: 110 },
    {
      headerName: 'Aika',
      field: 'start',
      width: 130,
      valueGetter: (value, row) => WEEKDAYS[seriesWeekday(row)] + ' ' + (row.start || '') + '–' + (row.end || ''),
    },
    {
      headerName: 'Toistuvuus',
      field: 'recurrency',
      width: 240,
      sortable: false,
      valueGetter: (value) =>
        value ? (INTERVAL_LABELS[value.interval] || value.interval || '') + ' ' + formatDate(value.startdate) + ' – ' + formatDate(value.enddate) : '',
    },
    { headerName: 'Tila', field: 'state', width: 100, valueFormatter: (value) => STATE_LABELS[value] || value || '' },
    {
      headerName: 'Kerrat',
      field: 'occurrences',
      width: 80,
      valueGetter: (value, row) => this.state.occurrenceCounts[row.id] || 0,
    },
    {
      headerName: '',
      field: 'notice',
      minWidth: 260,
      flex: 1,
      sortable: false,
      valueGetter: (value, row) => this.state.notices[row.id] || '',
    },
  ]

  renderEditor() {
    const { editing } = this.state
    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Button onClick={() => this.setState({ editing: null })}>Palaa toistuviin</Button>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              Luonnos: {editing.name && editing.name.fi}. Tallennus päivittää tämän sarjan; kerrat luodaan, kun sarja
              julkaistaan.
            </Typography>
          </Col>
        </Row>
        <ActivityCreator
          key={editing.id}
          recurrent
          initialActivity={editing}
          initialWeekday={seriesWeekday(editing)}
          onCreate={this.saveDraft}
          onSaved={() => this.setState({ editing: null })}
          activitytypes={this.state.activitytypes}
          sports={this.state.sports}
          images={this.state.images}
          customerdata={this.props.customerdata}
        />
      </div>
    )
  }

  render() {
    if (this.state.editing) return this.renderEditor()
    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Typography variant='body2' color='text.secondary'>
              Toistuvat sarjat. Luonnoksesta ei vielä ole kertoja aktiviteettilistassa: viimeistele ja julkaise se
              täältä. Julkaistun sarjan kertoja muokataan aktiviteettilistasta.
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col>
            <div style={{ backgroundColor: '#fff' }}>
              <DataGrid
                autoHeight
                density='compact'
                rows={this.state.series}
                columns={this.columns()}
                loading={this.state.loading}
                disableRowSelectionOnClick
                localeText={{ noRowsLabel: 'Ei toistuvia aktiviteetteja.' }}
              />
            </div>
          </Col>
        </Row>
        <Dialog open={!!this.state.deleteTarget} onClose={() => this.setState({ deleteTarget: null })}>
          <DialogTitle>Poistetaanko luonnos?</DialogTitle>
          <DialogContent>
            <Typography>{this.state.deleteTarget && this.state.deleteTarget.name ? this.state.deleteTarget.name.fi : ''}</Typography>
          </DialogContent>
          <DialogActions>
            <Button color='link' onClick={() => this.setState({ deleteTarget: null })}>
              Peruuta
            </Button>
            <Button color='danger' onClick={this.confirmDelete}>
              Poista
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    )
  }
}

// config/sports and config/activitytypes extend the built-in lists
const mergeOptions = (defaults, doc) => {
  const options = [...defaults]
  if (doc.exists && Array.isArray(doc.data().list)) options.push(...doc.data().list)
  const unique = [...new Map(options.map((item) => [item.key, item])).values()]
  return unique.sort((a, b) => a.label.localeCompare(b.label))
}

export default withFirebase(RecurringActivitiesContainer)
